interface VonqOption {
  id: number
  name: string
}

interface VonqOptionWithChildren extends VonqOption {
  children: VonqOptionWithChildren[]
}

const flatChildrenOption = (
  parent: string | null,
  option: VonqOptionWithChildren
): VonqOption[] => {
  const fullName = [parent, option.name].filter((t) => t !== null).join(' - ')
  const all = option.children.flatMap((child) => flatChildrenOption(fullName, child))
  all.unshift({
    id: option.id,
    name: fullName,
  })

  return all
}

export const IndustryOptions: VonqOption[] = [
  { id: 1, name: 'Information Technology' },
  { id: 2, name: 'Communications' },
  { id: 3, name: 'Manufacturing' },
  { id: 4, name: 'Energy' },
  { id: 5, name: 'Arts & Culture' },
  { id: 6, name: 'Charity and Not for Profit' },
  { id: 7, name: 'Leisure and Sport' },
  { id: 8, name: 'Government and Public Sector' },
  { id: 9, name: 'Fashion' },
  { id: 10, name: 'Advertising' },
  { id: 11, name: 'Real Estate' },
  { id: 12, name: 'Healthcare' },
  { id: 13, name: 'Recruitment' },
  { id: 14, name: 'Legal' },
  { id: 15, name: 'Entertainment' },
  { id: 16, name: 'Building and Construction' },
  { id: 17, name: 'Agriculture' },
  { id: 18, name: 'Insurance' },
  { id: 19, name: 'Banking' },
  { id: 20, name: 'Accounting' },
  { id: 21, name: 'Finance' },
  { id: 22, name: 'Automotive' },
  { id: 23, name: 'Science & Research' },
  { id: 24, name: 'Education' },
  { id: 26, name: 'Media' },
  { id: 27, name: 'Transport and Rail' },
  { id: 28, name: 'Property and Housing' },
  { id: 29, name: 'Generic' },
  { id: 30, name: 'Engineering' },
  { id: 31, name: 'Pharmaceuticals' },
  { id: 32, name: 'Aerospace' },
  { id: 33, name: 'Aviation' },
  { id: 34, name: 'Travel and Tourism' },
  { id: 35, name: 'Retail' },
  { id: 36, name: 'Telecommunications' },
  { id: 37, name: 'Health and Safety' },
  { id: 38, name: 'Language' },
  { id: 39, name: 'Maritime' },
  { id: 40, name: 'Hospitality' },
  { id: 41, name: 'Sustainability' },
  { id: 42, name: 'Food' },
  { id: 43, name: 'Fast Moving Consumer Goods' },
  { id: 44, name: 'Logistics & Supply Chain' },
  { id: 47, name: 'Multilingual' },
  { id: 48, name: 'Academic' },
  { id: 49, name: 'Facility Management' },
  { id: 52, name: 'E-commerce' },
  { id: 53, name: 'Chemical' },
  { id: 54, name: 'Water management' },
]

export const EducationLevelOptions: VonqOption[] = [
  {
    id: 1,
    name: [
      { languageCode: 'de_DE', value: 'Master / PhD / Doktor' },
      { languageCode: 'en_GB', value: 'Master / Post-Graduate / PhD' },
      { languageCode: 'nl_NL', value: 'Master / Postdoctoraal' },
    ],
  },
  {
    id: 2,
    name: [
      { languageCode: 'de_DE', value: 'Bachelor / Absolvent' },
      { languageCode: 'en_GB', value: 'Bachelor / Graduate' },
      { languageCode: 'nl_NL', value: 'HBO / Bachelor' },
    ],
  },
  {
    id: 3,
    name: [
      { languageCode: 'de_DE', value: 'Ausbildung' },
      { languageCode: 'en_GB', value: 'Vocational / Diploma / Associates degree' },
      { languageCode: 'nl_NL', value: 'MBO / Beroepssecundair onderwijs' },
    ],
  },
  {
    id: 4,
    name: [
      { languageCode: 'de_DE', value: 'Haupt-/ Realschulabschluss' },
      { languageCode: 'en_GB', value: 'GCSE / A-Level / Highschool / GED' },
      { languageCode: 'nl_NL', value: 'Middelbaar / Secundair onderwijs' },
    ],
  },
].map((option) => ({ id: option.id, name: option.name[1]!.value }))

export const SeniorityOptions: VonqOption[] = [
  {
    id: 1,
    name: [
      { languageCode: 'de_DE', value: 'Gesch\u00e4ftsf\u00fchrer / C-Level' },
      { languageCode: 'en_GB', value: 'Executive/Director' },
      { languageCode: 'nl_NL', value: 'Executive/Director' },
    ],
  },
  {
    id: 2,
    name: [
      { languageCode: 'de_DE', value: 'Manager' },
      { languageCode: 'en_GB', value: 'Manager' },
      { languageCode: 'nl_NL', value: 'Manager' },
    ],
  },
  {
    id: 3,
    name: [
      { languageCode: 'de_DE', value: 'Professional' },
      { languageCode: 'en_GB', value: 'Mid-Senior level' },
      { languageCode: 'nl_NL', value: 'Professional' },
    ],
  },
  {
    id: 4,
    name: [
      { languageCode: 'de_DE', value: 'Berufseinsteiger' },
      { languageCode: 'en_GB', value: 'Entry level/Graduate' },
      { languageCode: 'nl_NL', value: 'Entry level/Graduate' },
    ],
  },
  {
    id: 5,
    name: [
      { languageCode: 'de_DE', value: 'Student/Trainee' },
      { languageCode: 'en_GB', value: 'Student/Trainee' },
      { languageCode: 'nl_NL', value: 'Student/Trainee' },
    ],
  },
].map((option) => ({ id: option.id, name: option.name[1]!.value }))

export const JobFunctionOptions: VonqOption[] = [
  { id: 1, name: 'IT', children: [] },
  { id: 2, name: 'Human Resources', children: [] },
  { id: 3, name: 'Retail', children: [] },
  { id: 4, name: 'Management', children: [] },
  {
    id: 6,
    name: 'Software Development',
    children: [
      { id: 23, name: 'Web Development', children: [] },
      { id: 61, name: 'Embedded Software', children: [] },
    ],
  },
  { id: 7, name: 'Sales', children: [] },
  {
    id: 8,
    name: 'Education',
    children: [{ id: 5, name: 'Teaching', children: [] }],
  },
  {
    id: 9,
    name: 'Finance',
    children: [
      { id: 50, name: 'Banking', children: [] },
      { id: 36, name: 'Tax', children: [] },
      { id: 69, name: 'Controlling', children: [] },
      { id: 58, name: 'Actuaries', children: [] },
      { id: 12, name: 'Accounting/Auditing', children: [] },
    ],
  },
  { id: 10, name: 'Marketing', children: [] },
  { id: 11, name: 'Customer Service', children: [] },
  {
    id: 13,
    name: 'Legal',
    children: [{ id: 32, name: 'Criminology', children: [] }],
  },
  { id: 14, name: 'Recruitment', children: [] },
  { id: 15, name: 'Driving/Transport', children: [] },
  { id: 16, name: 'Planning/Logistics', children: [] },
  {
    id: 17,
    name: 'Engineering',
    children: [
      { id: 28, name: 'Aviation', children: [] },
      { id: 56, name: 'Chemical Engineering', children: [] },
      { id: 60, name: 'Electrical/Electronic Engineering', children: [] },
      { id: 54, name: 'Mechanical Engineering', children: [] },
      { id: 68, name: 'Civil Engineering', children: [] },
    ],
  },
  { id: 18, name: 'Administrative', children: [] },
  { id: 19, name: 'Property', children: [] },
  { id: 20, name: 'Food/Kitchen', children: [] },
  {
    id: 21,
    name: 'Healthcare',
    children: [
      { id: 38, name: 'Therapy', children: [] },
      { id: 39, name: 'Dentistry', children: [] },
      { id: 41, name: 'Fitness', children: [] },
      { id: 27, name: 'Nursing', children: [] },
      { id: 40, name: 'Nutrition', children: [] },
      { id: 43, name: 'Care', children: [] },
      { id: 42, name: 'Medical', children: [] },
      { id: 55, name: 'Surgery', children: [] },
      { id: 57, name: 'Psychology', children: [] },
    ],
  },
  { id: 22, name: 'Social Work', children: [] },
  {
    id: 24,
    name: 'Science and Research',
    children: [
      { id: 37, name: 'Chemistry', children: [] },
      { id: 51, name: 'Physics', children: [] },
      { id: 67, name: 'Biotechnology', children: [] },
      { id: 35, name: 'Pharmaceutical', children: [] },
      { id: 66, name: 'Biochemistry', children: [] },
      { id: 53, name: 'Biosciences', children: [] },
    ],
  },
  { id: 25, name: 'Coaching', children: [] },
  { id: 26, name: 'Design', children: [] },
  { id: 29, name: 'Operations', children: [] },
  { id: 31, name: 'Cleaning', children: [] },
  { id: 33, name: 'Translations', children: [] },
  { id: 34, name: 'Procurement', children: [] },
  {
    id: 48,
    name: 'Technology',
    children: [
      {
        id: 44,
        name: 'Data',
        children: [
          { id: 47, name: 'Big Data', children: [] },
          { id: 45, name: 'Data Analysis', children: [] },
          { id: 46, name: 'Data Engineering', children: [] },
          { id: 30, name: 'Data Science', children: [] },
        ],
      },
      { id: 49, name: 'Technology Management', children: [] },
    ],
  },
  { id: 52, name: 'PR/Communications', children: [] },
  { id: 59, name: 'Creative', children: [] },
  { id: 63, name: 'Quality Management', children: [] },
  { id: 64, name: 'Architecture', children: [] },
  { id: 65, name: 'Urban Planning', children: [] },
  { id: 70, name: 'Skilled Work', children: [] },
  { id: 71, name: 'Consultancy', children: [] },
].flatMap((option) => flatChildrenOption(null, option))
